/**
 * OrdersView - Order management
 *
 * Shows open orders with actions to cancel orders and place new ones.
 */

import { useState } from 'react';
import {
  Plus,
  X,
  Ban,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Download,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import type { KalshiOrder } from '@/lib/kalshiApi';
import type { PendingOrder } from '@/hooks/trading/useOrderManagement';
import { estimateFillTime, formatFillEta, type VelocityResult } from '@/lib/trading/queueVelocity';
import type { FillProbResult } from '@/lib/trading/fillProbability';
import { getOrderSort } from '@/lib/trading/orderSortPreference';
import { cn } from '@/lib/infra/utils';
import { Money } from '@/components/atoms/Money';
import { resolveDisplayTitle } from '@/lib/kalshiTicker/displayTitle';
import { ordersToCsv, downloadCsv } from '@/lib/infra/csvExport';

interface OrdersViewProps {
  orders: KalshiOrder[];
  pendingOrders?: PendingOrder[];
  velocityByOrderId?: Record<string, VelocityResult>;
  absoluteQueueByOrderId?: Record<string, number>;
  fillProbByOrderId?: Record<string, FillProbResult | null>;
  onNewOrder: () => void;
  onKillAll: () => void;
  onCancelOrder: (orderId: string) => void;
}

type SortKey =
  | 'created_time'
  | 'ticker'
  | 'side'
  | 'action'
  | 'yes_price'
  | 'remaining_count'
  | 'queue_position'
  | 'absolute_queue_depth'
  | 'fill_probability'
  | 'cost'
  | 'status';

export function OrdersView({
  orders,
  pendingOrders = [],
  velocityByOrderId = {},
  absoluteQueueByOrderId = {},
  fillProbByOrderId = {},
  onNewOrder,
  onKillAll,
  onCancelOrder,
}: OrdersViewProps) {
  const defaultSort = getOrderSort();
  const [sortKey, setSortKey] = useState<SortKey>(defaultSort.key as SortKey);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSort.direction);
  const [filter, setFilter] = useState<'all' | 'buy' | 'sell'>('all');
  const [cancellingOrder, setCancellingOrder] = useState<string | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(key);
    setSortDirection('desc');
  };

  const handleCancelOrder = async (orderId: string) => {
    setCancellingOrder(orderId);
    try {
      await onCancelOrder(orderId);
    } finally {
      setCancellingOrder(null);
    }
  };

  // Filter orders
  const filteredOrders = orders.filter((o) => {
    if (filter === 'all') return true;
    return o.action === filter;
  });

  // Sort orders
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const modifier = sortDirection === 'asc' ? 1 : -1;

    // Computed sort keys
    if (sortKey === 'absolute_queue_depth') {
      const aDepth = absoluteQueueByOrderId[a.order_id] ?? Infinity;
      const bDepth = absoluteQueueByOrderId[b.order_id] ?? Infinity;
      if (aDepth === Infinity && bDepth === Infinity) return 0;
      if (aDepth === Infinity) return 1;
      if (bDepth === Infinity) return -1;
      return (aDepth - bDepth) * modifier;
    }
    if (sortKey === 'fill_probability') {
      const aProb = fillProbByOrderId[a.order_id]?.probability ?? -1;
      const bProb = fillProbByOrderId[b.order_id]?.probability ?? -1;
      if (aProb === -1 && bProb === -1) return 0;
      if (aProb === -1) return 1;
      if (bProb === -1) return -1;
      return (aProb - bProb) * modifier;
    }
    if (sortKey === 'cost') {
      const aCost = getOrderPrice(a) * a.remaining_count;
      const bCost = getOrderPrice(b) * b.remaining_count;
      return (aCost - bCost) * modifier;
    }

    const aVal = a[sortKey];
    const bVal = b[sortKey];

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * modifier;
    }
    // Null/undefined sort to bottom regardless of direction
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    return ((aVal as number) - (bVal as number)) * modifier;
  });

  const buyOrders = orders.filter((o) => o.action === 'buy');
  const sellOrders = orders.filter((o) => o.action === 'sell');
  const getOrderPrice = (o: KalshiOrder) => (o.side === 'yes' ? o.yes_price : o.no_price) ?? 0;
  const totalBuyValue =
    buyOrders.reduce((sum, o) => sum + getOrderPrice(o) * o.remaining_count, 0) / 100;
  const totalSellValue =
    sellOrders.reduce((sum, o) => sum + getOrderPrice(o) * o.remaining_count, 0) / 100;

  const SortHeader = ({ label, sortKeyProp }: { label: string; sortKeyProp: SortKey }) => (
    <button
      onClick={() => handleSort(sortKeyProp)}
      className="hover:text-foreground flex items-center gap-1 transition-colors"
    >
      {label}
      {sortKey === sortKeyProp &&
        (sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
    </button>
  );

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="space-y-6 p-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="bg-card border-border rounded-lg border p-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wide">Open Orders</div>
          <div className="mt-1 text-2xl font-bold">{orders.length}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            <span className="text-green-500">{buyOrders.length} buy</span>
            {' / '}
            <span className="text-red-500">{sellOrders.length} sell</span>
          </div>
        </div>

        <div className="bg-card border-border rounded-lg border p-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wide">Buy Value</div>
          <Money
            value={totalBuyValue}
            className="mt-1 text-2xl font-bold text-green-500"
          />
        </div>

        <div className="bg-card border-border rounded-lg border p-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wide">Sell Value</div>
          <Money
            value={totalSellValue}
            className="mt-1 text-2xl font-bold text-red-500"
          />
        </div>

        <div className="bg-card border-border flex items-center justify-center gap-2 rounded-lg border p-4">
          <button
            onClick={onNewOrder}
            className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors"
          >
            <Plus size={14} />
            New Order
          </button>
          <button
            onClick={onKillAll}
            disabled={orders.length === 0}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Ban size={14} />
            Cancel All
          </button>
          <button
            onClick={() => {
              const csv = ordersToCsv(orders);
              const date = new Date().toISOString().slice(0, 10);
              downloadCsv(csv, `g1xl-orders-${date}.csv`);
            }}
            disabled={orders.length === 0}
            className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            title="Export orders CSV"
          >
            <Download size={14} />
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-card border-border rounded-lg border">
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b p-4">
          <h3 className="font-semibold">Open Orders</h3>

          {/* Filter tabs */}
          <div className="flex items-center gap-2">
            {(['all', 'buy', 'sell'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs transition-colors',
                  filter === f
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                {f === 'all' ? 'All' : f === 'buy' ? 'Buy Orders' : 'Sell Orders'}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {orders.length === 0 ? (
          <div className="text-muted-foreground p-8 text-center">
            <p className="text-sm">No open orders</p>
            <button
              onClick={onNewOrder}
              className="bg-primary text-primary-foreground hover:bg-primary/90 mt-4 rounded-lg px-4 py-2 text-sm transition-colors"
            >
              Place Your First Order
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-xs">
                  <th className="p-3 text-left font-medium">
                    <SortHeader
                      label="Time"
                      sortKeyProp="created_time"
                    />
                  </th>
                  <th className="p-3 text-left font-medium">
                    <SortHeader
                      label="Market"
                      sortKeyProp="ticker"
                    />
                  </th>
                  <th className="p-3 text-center font-medium">
                    <SortHeader
                      label="Action"
                      sortKeyProp="action"
                    />
                  </th>
                  <th className="p-3 text-center font-medium">
                    <SortHeader
                      label="Side"
                      sortKeyProp="side"
                    />
                  </th>
                  <th className="p-3 text-right font-medium">
                    <SortHeader
                      label="Price"
                      sortKeyProp="yes_price"
                    />
                  </th>
                  <th className="p-3 text-right font-medium">
                    <SortHeader
                      label="Qty"
                      sortKeyProp="remaining_count"
                    />
                  </th>
                  <th className="hidden p-3 text-right font-medium md:table-cell">
                    <SortHeader
                      label="Cost"
                      sortKeyProp="cost"
                    />
                  </th>
                  <th className="hidden p-3 text-center font-medium md:table-cell">
                    <SortHeader
                      label="Depth"
                      sortKeyProp="absolute_queue_depth"
                    />
                  </th>
                  <th className="hidden p-3 text-center font-medium md:table-cell">
                    <SortHeader
                      label="P(fill)"
                      sortKeyProp="fill_probability"
                    />
                  </th>
                  <th className="p-3 text-center font-medium">
                    <SortHeader
                      label="Status"
                      sortKeyProp="status"
                    />
                  </th>
                  <th className="p-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingOrders.map((po) => (
                  <tr
                    key={po.id}
                    className="border-border border-b opacity-60"
                  >
                    <td className="p-3">
                      <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
                        <Loader2
                          size={12}
                          className="animate-spin"
                        />
                        <span className="italic">Pending</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm italic">{resolveDisplayTitle(po.ticker)}</div>
                      <div className="text-muted-foreground font-mono text-[10px]">{po.ticker}</div>
                    </td>
                    <td className="p-3 text-center">
                      <span className="rounded bg-green-500/20 px-2 py-0.5 text-xs font-semibold text-green-400">
                        BUY
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span
                        className={cn(
                          'rounded px-2 py-0.5 text-xs font-semibold',
                          po.side === 'yes'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-orange-500/20 text-orange-400'
                        )}
                      >
                        {po.side.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-right text-sm font-medium">{po.priceCents}¢</td>
                    <td className="p-3 text-right text-sm font-medium">{po.contracts}</td>
                    <td className="hidden p-3 text-right text-sm font-medium md:table-cell">
                      <Money value={(po.priceCents * po.contracts) / 100} />
                    </td>
                    <td className="text-muted-foreground hidden p-3 text-center text-sm md:table-cell">
                      —
                    </td>
                    <td className="text-muted-foreground hidden p-3 text-center text-sm md:table-cell">
                      --
                    </td>
                    <td className="p-3 text-center">
                      <span className="rounded bg-blue-500/20 px-2 py-0.5 text-xs font-medium text-blue-400">
                        Pending
                      </span>
                    </td>
                    <td className="p-3 text-right" />
                  </tr>
                ))}
                {sortedOrders.map((order) => {
                  const isCancelling = cancellingOrder === order.order_id;

                  return (
                    <tr
                      key={order.order_id}
                      className="border-border hover:bg-muted/30 border-b transition-colors"
                    >
                      <td className="p-3">
                        <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
                          <Clock size={12} />
                          {formatTime(order.created_time)}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-sm">{resolveDisplayTitle(order.ticker)}</div>
                        <div className="text-muted-foreground font-mono text-[10px]">
                          {order.ticker}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className={cn(
                            'rounded px-2 py-0.5 text-xs font-semibold',
                            order.action === 'buy'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          )}
                        >
                          {order.action.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className={cn(
                            'rounded px-2 py-0.5 text-xs font-semibold',
                            order.side === 'yes'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-orange-500/20 text-orange-400'
                          )}
                        >
                          {order.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-3 text-right text-sm font-medium">
                        {getOrderPrice(order)}¢
                      </td>
                      <td className="p-3 text-right text-sm font-medium">
                        {order.remaining_count}
                      </td>
                      <td className="hidden p-3 text-right text-sm font-medium md:table-cell">
                        <Money value={(getOrderPrice(order) * order.remaining_count) / 100} />
                      </td>
                      <td className="hidden p-3 text-center text-sm md:table-cell">
                        <QueueDepthCell
                          orderId={order.order_id}
                          queuePosition={order.queue_position}
                          absoluteQueueByOrderId={absoluteQueueByOrderId}
                          velocityByOrderId={velocityByOrderId}
                        />
                      </td>
                      <td className="hidden p-3 text-center text-sm md:table-cell">
                        <FillProbBadge
                          result={fillProbByOrderId[order.order_id]}
                          ticker={order.ticker}
                        />
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className={cn(
                            'rounded px-2 py-0.5 text-xs font-medium',
                            order.status === 'resting' && 'bg-yellow-500/20 text-yellow-400',
                            order.status === 'pending' && 'bg-blue-500/20 text-blue-400',
                            order.status === 'filled' && 'bg-green-500/20 text-green-400',
                            order.status === 'cancelled' && 'bg-gray-500/20 text-gray-400'
                          )}
                        >
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => handleCancelOrder(order.order_id)}
                          disabled={isCancelling}
                          className={cn(
                            'text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded p-1.5 transition-colors',
                            isCancelling && 'cursor-not-allowed opacity-50'
                          )}
                          title="Cancel order"
                        >
                          {isCancelling ? (
                            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <X size={14} />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {sortedOrders.length > 0 && (
                <tfoot>
                  <tr className="border-border border-t">
                    <td
                      colSpan={6}
                      className="text-muted-foreground p-3 text-right text-xs font-medium"
                    >
                      Total Cost
                    </td>
                    <td className="hidden p-3 text-right text-sm font-bold md:table-cell">
                      <Money
                        value={
                          sortedOrders.reduce(
                            (sum, o) => sum + getOrderPrice(o) * o.remaining_count,
                            0
                          ) / 100
                        }
                        className="font-bold"
                      />
                    </td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function QueueDepthCell({
  orderId,
  queuePosition,
  absoluteQueueByOrderId,
  velocityByOrderId,
}: {
  orderId: string;
  queuePosition?: number;
  absoluteQueueByOrderId: Record<string, number>;
  velocityByOrderId: Record<string, VelocityResult>;
}) {
  const depth = absoluteQueueByOrderId[orderId];
  const velocity = velocityByOrderId[orderId];

  // Show absolute depth if available, fall back to queue_position
  const displayValue = depth ?? queuePosition;
  if (displayValue == null) return <span className="text-muted-foreground">—</span>;

  const VelocityIcon =
    velocity?.direction === 'advancing'
      ? TrendingUp
      : velocity?.direction === 'retreating'
        ? TrendingDown
        : Minus;
  const velocityColor =
    velocity?.direction === 'advancing'
      ? 'text-green-400'
      : velocity?.direction === 'retreating'
        ? 'text-red-400'
        : 'text-muted-foreground';

  // Compute fill ETA from depth + velocity
  const eta =
    displayValue != null && velocity ? estimateFillTime(displayValue, velocity.velocity) : null;

  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-muted-foreground font-mono">{displayValue.toLocaleString()}</span>
      {velocity && (
        <VelocityIcon
          size={12}
          className={velocityColor}
        />
      )}
      <span className="text-[10px] text-zinc-500">{formatFillEta(eta)}</span>
    </span>
  );
}

const FILL_BAND_COLORS: Record<string, string> = {
  high: 'text-green-400',
  medium: 'text-yellow-400',
  low: 'text-orange-400',
  'very-low': 'text-red-400',
};

function FillProbBadge({
  result,
  ticker,
}: {
  result: FillProbResult | null | undefined;
  ticker?: string;
}) {
  if (!result) {
    const isNba = ticker?.startsWith('KXNBAGAME') ?? false;
    const tip = isNba
      ? 'NBA moneyline — waiting for data (needs 1-12.5h to tipoff, orderbook, queue depth)'
      : 'Model only covers NBA moneyline (KXNBAGAME) orders';
    return (
      <span
        className="text-muted-foreground cursor-help text-xs"
        title={tip}
      >
        {isNba ? '...' : '—'}
      </span>
    );
  }
  const pct = Math.round(result.probability * 100);
  const color = FILL_BAND_COLORS[result.band];
  return (
    <span
      className={cn('font-mono text-xs', color)}
      title={`${result.level} model · ${result.inputs.hoursToTipoff.toFixed(1)}h to tip · ${result.inputs.contractsAhead} ahead · vel ${result.inputs.fillVel30m}/30m`}
    >
      {pct}%
    </span>
  );
}
